import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { Platform } from 'react-native';
import { useAuthStore } from '../stores/authStore';
import { useNetworkStore } from '../stores/networkStore';
import { API_TIMEOUT_MS } from '../constants';
import { addBreadcrumb, captureException } from '../observability/sentry';
import { classifyError, isTransientError } from '../utils/errors';
import { refreshTokens, sessionIdFrom } from '../auth/oidc';
import { refresh as refreshWebSession } from '../auth/webSession';
import { isStepUpChallenge } from '../auth/challenge';

/**
 * Correlation id for one API call, sent as X-Request-Id.
 *
 * crypto.randomUUID() is not universally present on the Hermes/React Native
 * global, so fall back to a random string. Uniqueness only has to hold within a
 * session's log window, not globally.
 */
function newRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Resolution order matters, and production is the case with NO env var set.
//
//  1. EXPO_PUBLIC_API_URL when present — native builds (per profile in
//     eas.json) and local web dev, where docker-compose.yml runs the Expo dev
//     server on :3000 with the API on :8000. Metro has no /api proxy (admin
//     gets one from vite.config.ts; Metro does not), so web dev genuinely
//     needs the absolute URL.
//  2. Relative on web — the production images bake no API URL, so the var is
//     unset and the SPA is served by the same FastAPI process that serves
//     /api/v1. Same-origin, which is what lets one cynosure-user image be
//     valid in every cluster.
//  3. Platform fallbacks for native dev with nothing configured.
const getBaseUrl = (): string => {
  const envUrl = process.env.EXPO_PUBLIC_API_URL;
  if (envUrl) return `${envUrl}/api/v1`;

  if (Platform.OS === 'web') return '/api/v1';

  return Platform.select({
    android: 'http://10.0.2.2:8000/api/v1', // Android emulator → host
    default: 'http://localhost:8000/api/v1',
  })!;
};

const BASE_URL = getBaseUrl();

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Automatic retry with exponential backoff for transient failures.
// Only retry idempotent verbs — retrying a POST on a 5xx could create
// duplicate resources if the server actually processed the first request
// before the failure.
axiosRetry(apiClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
});

// Request interceptor: attach auth token + a correlation id to every request
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // The stored token is a DEFAULT, not an override: a caller that set its own
    // Authorization header must win.
    //
    // ⚠️  This used to overwrite unconditionally, which broke login outright.
    //     `establishIdpSession` presents the access token it just exchanged,
    //     because the store does not hold it yet — but on web the OIDC callback
    //     is a full page load, so `authStore.initialize()` has already
    //     rehydrated the PREVIOUS session's token by the time it runs (it
    //     restores the value whatever its expiry, and only flips
    //     `isAuthenticated`). The interceptor then replaced the fresh token with
    //     the stale one, the ID token's `at_hash` no longer matched the access
    //     token it arrived with, and the API refused the pair with a 401 that
    //     says nothing — a sign-in loop that survives every retry, because the
    //     stale value is reread from storage each time.
    //
    //     Read with `has()` rather than `config.headers.Authorization`: this is
    //     an AxiosHeaders instance, whose lookups are case-insensitive, so
    //     property access is not a reliable way to ask the question.
    const { accessToken, idToken, sessionId } = useAuthStore.getState();
    if (accessToken && config.headers && !config.headers.has('Authorization')) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Which A3RN session this client belongs to, so the API can say when it
    // was ended elsewhere — see `isSessionEnded` below.
    // Web holds no ID token — the API hands the session id over directly.
    const sid = Platform.OS === 'web' ? sessionId : sessionIdFrom(idToken);
    if (sid && config.headers) {
      config.headers['X-IdP-Session'] = sid;
    }

    // Client-generated request id. The backend honours X-Request-Id when it is
    // printable and <=128 chars, binds it to every structlog line for that
    // request, and echoes it back (middleware/request_context.py) — so a client
    // error can be pivoted straight to the backend lines that served it:
    //   {namespace="cynosure"} | json | request_id="<id>"
    //
    // Generating it here rather than relying on the echoed response header is
    // what makes the FAILED requests correlatable: on a timeout or a network
    // drop there is no response to read a header from, and those are exactly
    // the cases worth investigating.
    const requestId = newRequestId();
    if (config.headers) {
      config.headers['X-Request-Id'] = requestId;
    }
    (config as { requestId?: string }).requestId = requestId;

    addBreadcrumb({
      category: 'http',
      message: `${config.method?.toUpperCase() ?? 'GET'} ${config.url ?? ''}`,
      level: 'info',
      // Method, path, and the id — never bodies or headers. Bodies on this app
      // are E2EE ciphertext or credentials; neither belongs in telemetry.
      data: { requestId },
    });

    return config;
  },
  (error) => Promise.reject(error)
);

// The API's answer to a request whose A3RN session ended elsewhere (back-channel
// logout, identity-architecture §6).
function isSessionEnded(error: AxiosError): boolean {
  if (error.response?.status !== 401) return false;
  const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
  return (
    typeof detail === 'object' &&
    detail !== null &&
    (detail as { code?: unknown }).code === 'idp_session_ended'
  );
}

// Response interceptor: handle 401 with token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Endpoints whose own 401 must NOT trigger a refresh-and-retry. Only one is
// left: a failure to establish the session IS the authentication failure, and
// retrying it after a refresh would loop. The rest of this list retired with
// Cynosure's own login endpoints in Phase 4.
const AUTH_ENDPOINTS = ['/auth/idp/session'];

// A step-up 401 must be recognised BEFORE the refresh branch below. It says
// "this token's authentication is too old or of the wrong kind", and a refresh
// answers neither: `auth_time` records when the USER authenticated, not when the
// token was minted, so a refreshed token carries exactly the same `auth_time`
// that just failed. Left to the refresh path the retry fails identically, the
// `catch` reads that as a dead session, and the user is logged out for asking to
// delete their account.

apiClient.interceptors.response.use(
  (response) => {
    // Any successful response means the API is reachable
    useNetworkStore.getState().markApiReachable();
    return response;
  },
  async (error: AxiosError) => {
    // Mark API unreachable on transient errors
    if (isTransientError(error)) {
      useNetworkStore.getState().markApiUnreachable();
    }
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const url = originalRequest?.url ?? '';
    const isAuthEndpoint = AUTH_ENDPOINTS.some((ep) => url.includes(ep));

    // Before the refresh branch, and for the opposite reason to the step-up one
    // below: here a refresh WOULD succeed — ZITADEL keeps a session's refresh
    // tokens alive after the session ends — and succeeding is the bug. The user
    // signed out in another A3RN app; revoke and sign out, don't refresh.
    if (isSessionEnded(error)) {
      await useAuthStore.getState().logoutSessionEnded();
      return Promise.reject(error);
    }

    // Before the refresh branch, deliberately — see isStepUpChallenge.
    if (isStepUpChallenge(error)) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const { refreshToken, setTokens, setWebSession, logout } = useAuthStore.getState();

      // Web: the refresh token is an HttpOnly cookie this code never sees, and
      // the API mints the new access token from it (src/auth/webSession.ts).
      if (Platform.OS === 'web') {
        try {
          const session = await refreshWebSession();
          if (!session) {
            // The API already holds no session (refused, expired or revoked),
            // so there is no ID token left to name it at the IdP — visiting
            // end_session without one strands the user on ZITADEL's picker.
            // Clear up and go to sign-in instead.
            processQueue(error, null);
            await useAuthStore.getState().logoutSessionEnded();
            return Promise.reject(error);
          }
          setWebSession(session.accessToken, session.sessionId);
          processQueue(null, session.accessToken);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${session.accessToken}`;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          // TransientSessionError: the API was unreachable, which says nothing
          // about the session. Keep the user signed in, as below.
          captureException(refreshError, { context: 'apiClient.webSessionRefresh' });
          useNetworkStore.getState().markApiUnreachable();
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (!refreshToken) {
        await logout();
        return Promise.reject(error);
      }

      try {
        // ⚠️  REFRESH HAPPENS AT THE IDENTITY PROVIDER, not here. This used to
        //     POST to Cynosure's own /auth/refresh, which hashed the token and
        //     looked it up in the `refresh_tokens` table — where an IdP refresh
        //     token has never been. The lookup missed, the endpoint answered
        //     401, and the `catch` below read that as a dead session and logged
        //     the user out. So an IdP session ended at every access-token
        //     expiry, looking exactly like an ordinary timeout.
        //
        //     The refreshed id_token is PASSED ON, and this call used to omit
        //     it on the belief that a refresh issues none. ZITADEL does issue
        //     one, and dropping it let the stored id_token_hint expire under a
        //     session that was still being refreshed — after which logout could
        //     no longer name a session to end and stopped at the provider's
        //     picker instead of returning to the app. `undefined` here still
        //     preserves the stored one, so the old property holds as a fallback.
        const refreshed = await refreshTokens(refreshToken);
        if (!refreshed) {
          await logout();
          return Promise.reject(error);
        }
        await setTokens(refreshed.accessToken, refreshed.refreshToken, refreshed.idToken);
        processQueue(null, refreshed.accessToken);
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${refreshed.accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        captureException(refreshError, { context: 'apiClient.tokenRefresh' });
        processQueue(refreshError, null);

        // Only logout on definitive auth failures (401/403 from refresh endpoint).
        // For transient errors (network down, timeout, 5xx), keep the user
        // authenticated so they can retry once connectivity is restored.
        if (!isTransientError(refreshError)) {
          await logout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Safety net: if any non-auth endpoint returns 404 "User not found",
    // the JWT references a deleted user — force logout.
    if (
      error.response?.status === 404 &&
      !isAuthEndpoint &&
      (error.response?.data as Record<string, unknown>)?.detail === 'User not found'
    ) {
      captureException(error, { context: 'apiClient.userNotFound' });
      await useAuthStore.getState().logout();
      return Promise.reject(error);
    }

    // Broad capture for API failures the UI shouldn't have received.
    // Filter rationale (matches the "network errors are noisy" constraint):
    //   server (5xx)  → forwarded (server bugs)
    //   client (4xx)  → forwarded (unexpected client errors)
    //   auth          → skipped (token-refresh path captures explicitly above)
    //   network/timeout → skipped (noise — common and occasionally expected)
    //   unknown       → skipped (not an AxiosError; render-path is covered by ErrorBoundary)
    const kind = classifyError(error);
    if (kind === 'server' || kind === 'client') {
      captureException(error, {
        context: 'apiClient.response',
        kind,
        status: error.response?.status,
        method: originalRequest?.method?.toUpperCase(),
        url: originalRequest?.url,
        // Prefer the echoed header (proves the backend saw this id), and fall
        // back to the one we generated — the fallback is what covers timeouts
        // and network drops, where no response header ever arrives.
        requestId:
          (error.response?.headers?.['x-request-id'] as string | undefined) ??
          (originalRequest as { requestId?: string } | undefined)?.requestId ??
          null,
      });
    }

    return Promise.reject(error);
  }
);

// ---- Auth API ----

export interface TokenResponse {
  access_token: string;
  refresh_token: string | null;
  token_type: string;
  expires_in: number;
  is_new_user: boolean;
  e2ee_setup_complete: boolean;
}

export const authApi = {
  /**
   * Exchange a verified identity-provider identity for a local Cynosure user,
   * creating it on first sight (identity-architecture §2).
   *
   * Called once per login, with the ID token in the body. The ID token is
   * needed because the access token carries no profile claims at all, so a new
   * account cannot be created from it.
   *
   * ⚠️  The access token is passed EXPLICITLY rather than left to the request
   *     interceptor. This runs before the auth store is populated — deliberately,
   *     since storing tokens first would flip isAuthenticated and start firing
   *     requests for a user row that does not exist yet — so the interceptor has
   *     nothing to attach and the call would arrive unauthenticated.
   *
   * Safe to call on every login: after the first it is a lookup and a profile
   * refresh, and `created` says which happened.
   */
  establishIdpSession: (idToken: string, accessToken: string) =>
    apiClient.post<{
      user_id: string;
      email: string;
      display_name: string | null;
      created: boolean;
    }>(
      '/auth/idp/session',
      { id_token: idToken },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    ),

};

// ---- User / Me API ----

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  email_verified: boolean;
  e2ee_setup_complete: boolean;
  is_admin: boolean;
  created_at: string;
}

export const userApi = {
  getMe: () => apiClient.get<User>('/me'),

  updateMe: (data: { email?: string; display_name?: string; settings?: object }) =>
    apiClient.put<User>('/me', data),

  // No reauth_token: the step-up proof rides in the access token and is read
  // by introspection at the identity provider (identity-architecture §4).
  deleteAccount: () =>
    apiClient.delete('/user', { data: { confirm_deletion: true } }),
};

// ---- Visions API ----

export interface EncryptionMetadata {
  iv?: string;
  title_iv?: string;
  metrics_iv?: string;
  start_date_iv?: string;
  end_date_iv?: string;
  estimated_minutes_iv?: string;
  future_letter_iv?: string;
}

export interface Vision {
  id: string;
  user_id: string;
  team_id: string | null;
  key_version: number;
  created_by_user_id: string | null;
  title_encrypted: string;
  description_encrypted: string;
  future_letter_encrypted: string | null;
  encryption_metadata: EncryptionMetadata;
  sort_order: number;
  created_at: string;
  updated_at: string | null;
}

export interface VisionImpact {
  vision_id: string;
  date: string;
  connected_activities: number;
  total_activities: number;
}

export const visionsApi = {
  list: (params?: { team_id?: string }) => apiClient.get<Vision[]>('/visions', { params }),

  get: (id: string) => apiClient.get<Vision>(`/visions/${id}`),

  create: (data: {
    title_encrypted: string;
    description_encrypted?: string;
    future_letter_encrypted?: string;
    encryption_metadata?: EncryptionMetadata;
    team_id?: string;
    key_version?: number;
  }) => apiClient.post<Vision>('/visions', data),

  update: (id: string, data: {
    title_encrypted?: string;
    description_encrypted?: string;
    future_letter_encrypted?: string;
    encryption_metadata?: EncryptionMetadata;
  }) => apiClient.put<Vision>(`/visions/${id}`, data),

  delete: (id: string) => apiClient.delete(`/visions/${id}`),

  impact: (id: string, date?: string) =>
    apiClient.get<VisionImpact>(`/visions/${id}/impact`, { params: date ? { date } : undefined }),
};

// ---- Goals API ----

export interface Goal {
  id: string;
  user_id: string;
  team_id: string | null;
  key_version: number;
  created_by_user_id: string | null;
  vision_id: string | null;
  title_encrypted: string;
  description_encrypted: string;
  encryption_metadata: EncryptionMetadata;
  metrics_encrypted: string | null;
  milestone_count: number | null;
  start_date_encrypted: string | null;
  end_date_encrypted: string | null;
  status: 'planned' | 'active' | 'completed' | 'paused';
  sort_order: number;
  created_at: string;
  updated_at: string | null;
}

export const goalsApi = {
  list: (params?: { team_id?: string }) => apiClient.get<Goal[]>('/goals', { params }),

  get: (id: string) => apiClient.get<Goal>(`/goals/${id}`),

  create: (data: {
    title_encrypted: string;
    description_encrypted?: string;
    encryption_metadata?: EncryptionMetadata;
    vision_id?: string;
    status?: string;
    start_date_encrypted?: string;
    end_date_encrypted?: string;
    metrics_encrypted?: string;
    milestone_count?: number;
    team_id?: string;
    key_version?: number;
  }) => apiClient.post<Goal>('/goals', data),

  update: (
    id: string,
    data: {
      title_encrypted?: string;
      description_encrypted?: string;
      encryption_metadata?: EncryptionMetadata;
      vision_id?: string | null;
      status?: string;
      start_date_encrypted?: string;
      end_date_encrypted?: string;
      metrics_encrypted?: string;
      milestone_count?: number;
    }
  ) => apiClient.put<Goal>(`/goals/${id}`, data),

  updateStatus: (id: string, status: string) =>
    apiClient.patch<Goal>(`/goals/${id}/status`, { status }),

  delete: (id: string) => apiClient.delete(`/goals/${id}`),
};

// ---- Activities API ----

export interface Activity {
  id: string;
  user_id: string;
  team_id: string | null;
  key_version: number;
  created_by_user_id: string | null;
  goal_id: string | null;
  recurrence_schedule_id: string | null;
  is_exception: boolean;
  exception_from_schedule_id: string | null;
  title_encrypted: string;
  description_encrypted: string;
  encryption_metadata: EncryptionMetadata;
  status: 'todo' | 'in-progress' | 'done' | 'skipped';
  estimated_minutes_encrypted: string | null;
  start_time: string;
  completed_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string | null;
}

export const activitiesApi = {
  list: (params?: { status?: string; goal_id?: string; date?: string; team_id?: string }) =>
    apiClient.get<Activity[]>('/activities', { params }),

  get: (id: string) => apiClient.get<Activity>(`/activities/${id}`),

  create: (data: {
    title_encrypted: string;
    description_encrypted?: string;
    encryption_metadata?: EncryptionMetadata;
    goal_id?: string;
    status?: string;
    estimated_minutes_encrypted?: string;
    start_time: string;
    recurrence_schedule_id?: string;
    team_id?: string;
    key_version?: number;
  }) => apiClient.post<Activity>('/activities', data),

  update: (
    id: string,
    data: {
      title_encrypted?: string;
      description_encrypted?: string;
      encryption_metadata?: EncryptionMetadata;
      goal_id?: string | null;
      status?: string;
      estimated_minutes_encrypted?: string;
      start_time?: string;
      edit_mode?: 'this' | 'future';
    }
  ) => apiClient.put<Activity>(`/activities/${id}`, data),

  updateStatus: (id: string, status: string) =>
    apiClient.patch(`/activities/${id}/status`, { status }),

  delete: (id: string, deleteMode?: 'this' | 'future') =>
    apiClient.delete(`/activities/${id}`, {
      ...(deleteMode ? { params: { delete_mode: deleteMode } } : {}),
    }),
};

// ---- Recurrence Schedules API ----

export interface RecurrenceSchedule {
  id: string;
  user_id: string | null;
  activity_template_id: string | null;
  rrule: string;
  schedule_start: string;
  schedule_end: string | null;
  timezone_created_encrypted: string | null;
  timezone_latest_encrypted: string | null;
  encryption_metadata: EncryptionMetadata;
  is_active: boolean;
  team_id: string | null;
  key_version: number;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string | null;
}

export const recurrenceSchedulesApi = {
  get: (id: string) =>
    apiClient.get<RecurrenceSchedule>(`/recurrence-schedules/${id}`),

  create: (data: {
    rrule: string;
    schedule_start: string;
    schedule_end?: string;
    activity_template_id?: string;
    timezone_created_encrypted?: string;
    timezone_latest_encrypted?: string;
    encryption_metadata?: EncryptionMetadata;
    team_id?: string;
    key_version?: number;
  }) => apiClient.post<RecurrenceSchedule>('/recurrence-schedules', data),

  update: (id: string, data: {
    rrule?: string;
    schedule_start?: string;
    schedule_end?: string;
    is_active?: boolean;
    timezone_created_encrypted?: string;
    timezone_latest_encrypted?: string;
    encryption_metadata?: EncryptionMetadata;
  }) => apiClient.put<RecurrenceSchedule>(`/recurrence-schedules/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/recurrence-schedules/${id}`),

  getInstances: (id: string) =>
    apiClient.get<Activity[]>(`/recurrence-schedules/${id}/instances`),

  list: (params?: { team_id?: string }) =>
    apiClient.get<RecurrenceSchedule[]>('/recurrence-schedules', { params }),

  materialize: (scheduleId: string, data: { occurrence_time: string; status?: string }) =>
    apiClient.post<Activity>(`/recurrence-schedules/${scheduleId}/materialize`, data),
};

// ---- Settings API ----

export interface Settings {
  diagnostics_opt_in: boolean;
  archive_retention_days: number;
  ai_enabled: boolean;
  ai_acknowledged: boolean;
  custom_prompts: Record<string, string> | null;
  notifications_enabled: boolean;
  notification_preferences: Record<string, boolean> | null;
}

export const settingsApi = {
  get: () => apiClient.get<Settings>('/settings'),

  update: (data: {
    diagnostics_opt_in?: boolean;
    archive_retention_days?: number;
    ai_enabled?: boolean;
    ai_acknowledged?: boolean;
    custom_prompts?: Record<string, string> | null;
    notifications_enabled?: boolean;
    notification_preferences?: Record<string, boolean> | null;
  }) => apiClient.put<Settings>('/settings', data),
};

// ---- Subscription API ----

export interface Subscription {
  id: string;
  user_id: string;
  /** 'free' or 'enterprise'. */
  tier: string;
  subscription_started_at: string | null;
  subscription_ends_at: string | null;
  /**
   * Written now: 'admin_manual', 'offline', 'enterprise_group'.
   * Still present on historical rows: 'iap', 'web', 'access_code'. Migration
   * 005 deliberately left those rather than flattening them, so anything
   * rendering this has to handle them — see planSourceLabel in settings.
   */
  subscription_source: string | null;
}

export const subscriptionApi = {
  // Read-only, and deliberately so. Nothing is sold from inside the app: the
  // account is free, and an Enterprise term is granted by an admin and lapses
  // unless one renews it. Settings reads this only to decide whether to offer
  // "Manage Enterprise Group".
  get: () => apiClient.get<Subscription>('/subscription'),
};

// ---- Meta API ----

/** Currently-released version of each client surface. See useVersionStatus. */
export interface Meta {
  web_release: string;
  ios_release: string;
  android_release: string;
}

export const metaApi = {
  // Unauthenticated — a client has to be able to tell the user it is out of
  // date before it holds a token.
  get: () => apiClient.get<Meta>('/meta'),
};

// ---- Contacts API ----

export interface Contact {
  id: string;
  user_id: string;
  category: 'bug' | 'feature' | 'billing' | 'account' | 'technical' | 'general';
  subject: string;
  message: string;
  app_version: string;
  platform: string;
  created_at: string;
}

export const contactsApi = {
  submit: (data: {
    category: string;
    subject: string;
    message: string;
    app_version: string;
    platform: string;
  }) => apiClient.post<Contact>('/contacts', data),
};

// ---- Export API ----

export interface ExportRequest {
  format?: string;
  include_encrypted?: boolean;
  include_audit_logs?: boolean;
  reauth_token?: string;
}

export interface ExportResponse {
  export_id: string;
  status: string;
  download_url: string | null;
  download_expires_at: string | null;
  progress_percent: number;
  estimated_size_bytes: number | null;
}

export const exportApi = {
  create: (data?: ExportRequest) =>
    apiClient.post<ExportResponse>('/user/export', data ?? {}),

  getStatus: (jobId: string) =>
    apiClient.get<ExportResponse>(`/user/export/${jobId}`),

  download: (jobId: string) =>
    apiClient.get(`/user/export/${jobId}/download`),
};

// ---- Sessions & Login History API ----

// ---- Encryption API ----

export const encryptionApi = {
  setup: (data: {
    salt: string;
    recovery_phrase_hash: string;
    e2ee_public_key?: string;
    personal_team_wrapped_key?: string;
  }) =>
    apiClient.post<{ success: boolean; e2ee_setup_complete: boolean }>(
      '/encryption/setup',
      data
    ),

  verifyPhrase: (data: { recovery_phrase_hash: string }) =>
    apiClient.post<{
      valid: boolean;
      salt: string | null;
      /**
       * Every wrapped team key the user holds, so restore can skip the per-team
       * key fetches. Null when the phrase was wrong.
       */
      team_keys: { team_id: string; key_version: number; wrapped_key: string }[] | null;
    }>('/encryption/verify-phrase', data),

  getStatus: () =>
    apiClient.get<{
      e2ee_setup_complete: boolean;
      e2ee_setup_at: string | null;
    }>('/encryption/status'),

  resetEncryption: (data: { confirmation: string }) =>
    apiClient.post<{
      success: boolean;
      message: string;
      items_deleted: Record<string, number>;
    }>('/encryption/reset', data),
};

// ---- Enterprise Group API ----

export interface EnterpriseGroup {
  id: string;
  owner_user_id: string;
  name: string | null;
  max_members: number;
  member_count: number;
  created_at: string;
}

export interface EnterpriseGroupMember {
  id: string;
  email: string;
  user_id: string | null;
  status: 'invited' | 'active' | 'removed';
  display_name: string | null;
  added_at: string;
  activated_at: string | null;
}

export interface EnterpriseGroupMemberListResponse {
  members: EnterpriseGroupMember[];
  total: number;
  limit: number;
  offset: number;
}

export interface EnterpriseGroupMemberAddResponse {
  added: EnterpriseGroupMember[];
  already_exists: string[];
  errors: Array<{ email: string; reason: string }>;
}

export const enterpriseGroupApi = {
  get: () =>
    apiClient.get<EnterpriseGroup>('/enterprise-group'),

  update: (data: { name?: string }) =>
    apiClient.patch<EnterpriseGroup>('/enterprise-group', data),

  listMembers: (params?: { search?: string; status?: string; limit?: number; offset?: number }) =>
    apiClient.get<EnterpriseGroupMemberListResponse>('/enterprise-group/members', { params }),

  addMembers: (emails: string[]) =>
    apiClient.post<EnterpriseGroupMemberAddResponse>('/enterprise-group/members', { emails }),

  removeMember: (memberId: string) =>
    apiClient.delete(`/enterprise-group/members/${memberId}`),

  transfer: (newOwnerEmail: string) =>
    apiClient.post<EnterpriseGroup>('/enterprise-group/transfer', { new_owner_email: newOwnerEmail }),

  // Seat expansion is no longer self-serve: Enterprise is invoiced offline, so
  // owners request more seats via sales and an admin applies them.
};

// ---- Teams API ----

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: string;
  display_name: string | null;
  email: string | null;
  joined_at: string;
}

export interface TeamMembersResponse {
  members: TeamMember[];
}

export interface AddMemberResponse {
  member: TeamMember;
  public_key: string; // base64 X25519 for client-side key wrapping
}

export interface MemberWrappedKey {
  user_id: string;
  wrapped_key: string; // base64
}

export const teamsApi = {
  getMyTeams: () => apiClient.get('/teams/my-teams'),
  getTeam: (id: string) => apiClient.get(`/teams/${id}`),
  getTeamKeys: (teamId: string) => apiClient.get(`/teams/${teamId}/keys`),
  uploadKey: (teamId: string, data: { key_version: number; wrapped_key: string }) =>
    apiClient.post(`/teams/${teamId}/keys`, data),

  createTeam: (data: { name_encrypted: string; encryption_metadata: object }) =>
    apiClient.post('/teams', data),

  getTeamMembers: (teamId: string) =>
    apiClient.get<TeamMembersResponse>(`/teams/${teamId}/members`),

  addMember: (teamId: string, email: string) =>
    apiClient.post<AddMemberResponse>(`/teams/${teamId}/members`, { email }),

  removeMember: (teamId: string, userId: string) =>
    apiClient.delete(`/teams/${teamId}/members/${userId}`),

  rotateKeys: (teamId: string, data: { key_version: number; wrapped_keys: MemberWrappedKey[] }) =>
    apiClient.post(`/teams/${teamId}/rotate-key`, data),

  updateTeam: (teamId: string, data: { name_encrypted: string; encryption_metadata: object }) =>
    apiClient.patch(`/teams/${teamId}`, data),

  transferOwnership: (teamId: string, newOwnerUserId: string) =>
    apiClient.post(`/teams/${teamId}/transfer-ownership`, {
      new_owner_user_id: newOwnerUserId,
    }),

  getMemberPublicKeys: (teamId: string) =>
    apiClient.get<{ keys: { user_id: string; public_key: string }[] }>(
      `/teams/${teamId}/members/public-keys`,
    ),

  deleteTeam: (teamId: string) =>
    apiClient.delete(`/teams/${teamId}`),
};

// ---- Notifications API ----

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  is_read: boolean;
  read_at: string | null;
  action_url: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  items: AppNotification[];
  total: number;
  unread_count: number;
}

export const notificationsApi = {
  list: (params?: { unread_only?: boolean; limit?: number; offset?: number }) =>
    apiClient.get<NotificationListResponse>('/notifications', { params }),

  getUnreadCount: () =>
    apiClient.get<{ unread_count: number }>('/notifications/unread-count'),

  markRead: (notificationIds?: string[]) =>
    apiClient.post('/notifications/mark-read', {
      notification_ids: notificationIds ?? null,
    }),

  delete: (id: string) => apiClient.delete(`/notifications/${id}`),
};

export default apiClient;
